<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: CalendarWidget                                        |
| Section: components                                              |
| Purpose: Event heatmap calendar with daily activity intensity.   |
+------------------------------------------------------------------+
-->
<template>
    <div class="calendar-widget-container">
        <div class="calendar-widget">
            <!-- Loop over each month -->
            <div class="month-group" v-for="month in yearData" :key="month.month">
                <div class="month-label">{{ monthLabels[month.month - 1] }}</div>
                <div class="month-grid">
                    <!-- Loop over each day in the month -->
                    <div v-for="day in month.days" :key="day.date" class="day-block"
                        :class="{ selected: isSelected(day) }" :style="{ backgroundColor: getColor(day.eventCount) }"
                        @click="selectDate(day)" @mouseenter="showTooltip(day, $event)" @mouseleave="hoveredDay = null">
                        {{ day.day }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Heatmap Legend -->
        <div class="heatmap-legend">
            <span class="legend-label">{{ $t('eventCalendar.legendLess') }}</span>
            <span v-for="(color, i) in legendColors" :key="i" class="legend-swatch"
                :style="{ backgroundColor: color }"></span>
            <span class="legend-label">{{ $t('eventCalendar.legendMore') }}</span>
        </div>

        <!-- Hover Tooltip -->
        <div class="calendar-tooltip" v-if="hoveredDay" :style="tooltipStyle">
            <strong>{{ hoveredDay.day }} {{ monthLabels[parseInt(hoveredDay.date.split('-')[0]) - 1] }}</strong>
            <span>{{ hoveredDay.eventCount }} record{{ hoveredDay.eventCount === 1 ? '' : 's' }}</span>
        </div>
    </div>
</template>

<script>
import { fetchRuntimeJson } from '@/utils/runtimeJson';

export default {
    name: "CalendarWidget",
    // Optionally, allow the parent to pass in a selected date (in "MM-DD" format)
    props: {
        selectedDate: {
            type: String,
            default: ''
        },
        overrideEventCounts: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            year: new Date().getFullYear(),
            // Month labels (you can adjust these to your locale)
            monthLabels: [
                "Jan", "Feb", "Mrt", "Apr", "Mei", "Jun",
                "Jul", "Aug", "Sep", "Okt", "Nov", "Dec"
            ],
            // This will be an array of month objects; each month holds an array of day objects.
            yearData: [],
            cachedEventCounts: [],
            hoveredDay: null,
            tooltipStyle: {},
            legendColors: ['#fff', '#fccdd2', '#f08080', '#eb5a5e', '#8c0f1c']
        };
    },
    computed: {
        // Compute the maximum event count across all days (used for scaling the colours)
        maxEventCount() {
            let max = 0;
            this.yearData.forEach((month) => {
                month.days.forEach((day) => {
                    if (day.eventCount > max) {
                        max = day.eventCount;
                    }
                });
            });
            return max;
        }
    },
    watch: {
        overrideEventCounts() {
            this.applyCachedEventCounts();
        }
    },
    methods: {
        // Load event counts for every day in the year.
        // (Be aware that making hundreds of API calls is not ideal for production.)
        applyCachedEventCounts() {
            this.yearData.forEach((month) => {
                month.days.forEach((day) => {
                    if (this.overrideEventCounts !== null) {
                        day.eventCount = this.overrideEventCounts[day.date] || 0;
                    } else {
                        const record = this.cachedEventCounts.find(item => item.date === day.date);
                        day.eventCount = record ? record.eventCount : 0;
                    }
                });
            });
        },
        async loadCachedEventCounts() {
            const counts = await fetchRuntimeJson('/data/getEventCounts/eventCounts.json', []);
            this.cachedEventCounts = Array.isArray(counts) ? counts : [];
            this.applyCachedEventCounts();
        },
        // async loadEventCounts() {
        //     const promises = [];
        //     this.yearData.forEach((month) => {
        //         month.days.forEach((day) => {
        //             // Call your API using the day’s date (in "MM-DD" format)
        //             const promise = fetchTodayEvents(day.date)
        //                 .then((response) => {
        //                     // Make sure the response is an array (as in your EventCalendar.vue)
        //                     day.eventCount = Array.isArray(response) ? response.length : 0;
        //                 })
        //                 .catch((err) => {
        //                     console.error(`Error fetching events for ${day.date}:`, err);
        //                     day.eventCount = 0;
        //                 });
        //             promises.push(promise);
        //         });
        //     });
        //     await Promise.all(promises);
        // },
        // Compute a background colour for a day based on its event count.
        // This uses a discrete scale similar to GitHub’s contribution graph.
        getColor(count) {
            if (count === 0) return "#ebedf0"; // Light gray (no events)
            const ratio = count / this.maxEventCount;

            if (ratio <= 0.1) return "#fde6e8";  // Very pale red
            else if (ratio <= 0.2) return "#fccdd2";  // Light pink
            else if (ratio <= 0.3) return "#f9b3b6";  // Soft pink
            else if (ratio <= 0.4) return "#f08080";  // Pastel red
            else if (ratio <= 0.5) return "#eb5a5e";  // Soft red
            else if (ratio <= 0.6) return "#e63946";  // Mid red
            else if (ratio <= 0.7) return "#d62737";  // Deep red
            else if (ratio <= 0.8) return "#c91b2b";  // Your original color
            else if (ratio <= 0.9) return "#a9141f";  // Dark red
            else return "#8c0f1c";  // Deepest red (high event density)
        },
        // When a day is clicked, emit an event to the parent so it can update the selected date.
        selectDate(day) {
            // day.date is in "MM-DD" format.
            // You could also emit an object with both month and day if needed.
            this.$emit("dateSelected", day.date);
        },
        // Check whether this day is the currently selected date.
        isSelected(day) {
            return this.selectedDate && day.date === this.selectedDate;
        },
        showTooltip(day, event) {
            this.hoveredDay = day;
            const x = event.clientX;
            const y = event.clientY;
            const tooltipWidth = 150;
            const left = x + tooltipWidth + 16 > window.innerWidth ? x - tooltipWidth - 8 : x + 12;
            this.tooltipStyle = {
                top: (y - 44) + 'px',
                left: left + 'px'
            };
        }
    },
    async mounted() {
        // Generate the data structure for the year.
        for (let m = 1; m <= 12; m++) {
            const daysInMonth = new Date(this.year, m, 0).getDate();
            const monthData = { month: m, days: [] };
            for (let d = 1; d <= daysInMonth; d++) {
                const mm = String(m).padStart(2, "0");
                const dd = String(d).padStart(2, "0");
                monthData.days.push({
                    day: d,
                    date: `${mm}-${dd}`,
                    eventCount: 0 // will be updated from the cached JSON data
                });
            }
            this.yearData.push(monthData);
        }
        await this.loadCachedEventCounts();
    }

};
</script>

<style scoped>
.calendar-widget-container {
    position: relative;
    margin-top: 48px;
}

.calendar-widget {
    display: flex;
    flex-direction: row;
    gap: 1px;
}

/* Month label (e.g. "Jan") */
.month-label {
    font-size: 12px;
    margin-bottom: 2px;
}

/* The grid for the days of the month */
.month-grid {
    display: flex;
    flex-wrap: wrap;
}

/* Each day block */
.day-block {
    width: 25%;
    height: 27px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    cursor: pointer;
    user-select: none;
}

/* Highlight the selected day */
.day-block:hover,
.day-block.selected {
    box-shadow: inset 0 0 0 2px var(--brand-text-dark);
}

/* Heatmap Legend */
.heatmap-legend {
    display: flex;
    align-items: center;
    gap: 3px;
    margin-top: 6px;
    justify-content: flex-end;
}

.legend-label {
    font-size: 10px;
    color: var(--brand-text-dark);
}

.legend-swatch {
    width: 12px;
    height: 12px;
    border-radius: 2px;
    display: inline-block;
}

/* Hover Tooltip */
.calendar-tooltip {
    position: fixed;
    pointer-events: none;
    background: var(--brand-bg-4);
    color: var(--brand-text-light);
    border-radius: var(--border-radius-sm);
    padding: 6px 10px;
    font-size: 12px;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 2px;
    box-shadow: var(--shadow-card-sm);
    white-space: nowrap;
}

@media (max-width: 1280px) {
    .day-block {
        height: 23px;
    }
}

@media (max-width: 1080px) {
    .calendar-widget-container {
        margin-top: 24px;
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;
        padding-bottom: 8px;
    }

    .calendar-widget {
        width: max-content;
        min-width: 900px;
        gap: 4px;
    }

    .month-label {
        font-size: 11px;
        text-align: center;
    }

    .day-block {
        width: 20px;
        height: 20px;
        font-size: 9px;
    }

    .heatmap-legend {
        justify-content: flex-start;
        flex-wrap: wrap;
        gap: 4px;
    }
}

@media (max-width: 640px) {
    .calendar-widget {
        min-width: 760px;
    }

    .day-block {
        width: 18px;
        height: 18px;
        font-size: 8px;
    }

    .calendar-tooltip {
        display: none;
    }
}
</style>
